import type {
  WikiPage,
  WikiDeletionState,
  WikiMode,
  WikiPageAddress,
  WikiLinkRenderer,
} from "../wiki-types";
import { Button } from "@softmax/design-system";
import { CommunityMarkdown } from "@softmax/web-ux/prose";
import { WikiPageByline } from "./wiki-page-byline";
import { WikiPageDeletion } from "./wiki-page-deletion";
import { styles } from "../wiki.styles";
import * as stylex from "@stylexjs/stylex";
import { spacing, colors, radii } from "@softmax/design-system/tokens.stylex";

export function WikiPageRead({
  missing,
  currentPage,
  canEdit,
  canDelete,
  onModeChange,
  renderLink,
  revisionCount,
  deletion,
  mutationError,
  fieldId,
  wikiPageHref,
  existingPageSlugs,
}: Readonly<{
  missing: boolean;
  currentPage: WikiPage | null;
  canEdit: boolean;
  canDelete: boolean;
  onModeChange(mode: WikiMode): void;
  renderLink: WikiLinkRenderer;
  revisionCount: number | null;
  deletion: WikiDeletionState;
  mutationError: string | null;
  fieldId: string;
  wikiPageHref: WikiPageAddress;
  existingPageSlugs: ReadonlySet<string>;
}>) {
  return (
    <>
      {" "}
      {missing && (
        <section {...stylex.props(localStyles.missing)}>
          <p {...stylex.props(styles.bodyText)}>
            This page hasn’t been written yet.
          </p>
          <p {...stylex.props(styles.smallMuted)}>
            {canEdit
              ? "You can write this page."
              : "You do not have permission to edit this wiki."}
          </p>
          <Button
            variant="primary"
            disabled={!canEdit}
            onClick={() => onModeChange("edit")}
          >
            Write this page
          </Button>
        </section>
      )}
      {currentPage && (
        <>
          <WikiPageByline
            revision={currentPage.currentRevision}
            renderLink={renderLink}
            revisionCount={revisionCount}
            onShowHistory={() => onModeChange("history")}
          />
          {canDelete && (
            <WikiPageDeletion
              deletion={deletion}
              mutationError={mutationError}
              fieldId={fieldId}
            />
          )}
          <div {...stylex.props(localStyles.prose)}>
            <CommunityMarkdown
              body={currentPage.currentRevision.body}
              wikiPageHref={wikiPageHref}
              existingPageSlugs={existingPageSlugs}
              demoteHeadings
              headingSlugs
              renderLink={renderLink}
            />
          </div>
        </>
      )}
    </>
  );
}
const localStyles = stylex.create({
  prose: { maxWidth: "68ch", marginTop: spacing.space20 },
  missing: {
    display: "flex",
    alignItems: "start",
    flexDirection: "column",
    gap: spacing.space12,
    padding: spacing.space24,
    marginTop: spacing.space24,
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: colors.borderStrong,
    borderRadius: radii.medium,
  },
});
